import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DashboardApi, DashboardConfig } from '../services/api';
import './OnlineMeetingSection.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

function extractEmail(value) {
  return String(value || '')
    .replace(/[,;\s]+$/, '')
    .trim();
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const OnlineMeetingSection = ({
  draft = {},
  onUpdateDraft,
  companyId,
  disabled = false,
  saving = false,
  assigneeEmail = '',
}) => {
  const [googleConfig, setGoogleConfig] = useState(null);
  const [msConnected, setMsConnected] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [customEmail, setCustomEmail] = useState('');
  const [customVerifying, setCustomVerifying] = useState(false);
  const [guestInputValue, setGuestInputValue] = useState('');
  const [showGuestInput, setShowGuestInput] = useState(false);
  const guestInputRef = useRef(null);

  const meetingProvider = draft?.meetingProvider || null;
  const guests = Array.isArray(draft?.meetingGuests) ? draft.meetingGuests : [];
  const description = String(draft?.meetingDescription || '');
  const impersonatedEmail = String(draft?.googleImpersonatedEmail || '');
  const savedMeetUrl = meetingProvider === 'google'
    ? String(draft?.googleMeetUrl || '')
    : meetingProvider === 'microsoft'
      ? String(draft?.microsoftTeamsUrl || '')
      : '';

  const provider = useCallback((p) => {
    onUpdateDraft({
      meetingProvider: p,
      meetingUrl: '',
      googleMeetUrl: '',
      microsoftTeamsUrl: '',
      googleImpersonatedEmail: '',
    });
    setEmailStatus(null);
    setCustomEmail('');
  }, [onUpdateDraft]);

  useEffect(() => {
    if (!companyId) return;
    let mounted = true;
    DashboardApi.getGoogleServiceAccount(companyId)
      .then((data) => { if (mounted) setGoogleConfig(data); })
      .catch(() => { if (mounted) setGoogleConfig({ configured: false }); });
    return () => { mounted = false; };
  }, [companyId]);

  useEffect(() => {
    DashboardApi.getMicrosoftStatus()
      .then((data) => setMsConnected(data?.connected || false))
      .catch(() => setMsConnected(false));
  }, []);

  const verifyEmail = useCallback(
    async (email) => {
      if (!email || !companyId) return;
      setVerifying(true);
      setEmailStatus(null);
      try {
        const result = await DashboardApi.verifyGoogleWorkspaceEmail(companyId, email);
        if (result.valid) {
          setEmailStatus({ valid: true, email });
          onUpdateDraft({ googleImpersonatedEmail: email });
        } else {
          setEmailStatus({ valid: false, email });
          setCustomEmail(email);
        }
      } catch {
        setEmailStatus({ valid: false });
      } finally {
        setVerifying(false);
      }
    },
    [companyId, onUpdateDraft],
  );

  useEffect(() => {
    if (meetingProvider !== 'google' || !googleConfig?.configured || emailStatus) return;
    const email = impersonatedEmail || assigneeEmail;
    if (email) verifyEmail(email);
  }, [meetingProvider, googleConfig, assigneeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomVerify = async () => {
    const email = extractEmail(customEmail);
    if (!email || !isValidEmail(email)) return;
    setCustomVerifying(true);
    try {
      const result = await DashboardApi.verifyGoogleWorkspaceEmail(companyId, email);
      if (result.valid) {
        setEmailStatus({ valid: true, email });
        onUpdateDraft({ googleImpersonatedEmail: email });
        setCustomEmail('');
      } else {
        setEmailStatus({ valid: false, error: 'E-postadressen ble ikke funnet i Google Workspace.' });
      }
    } catch {
      setEmailStatus({ valid: false, error: 'Kunne ikke verifisere e-postadressen.' });
    } finally {
      setCustomVerifying(false);
    }
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomVerify();
    }
  };

  const handleMicrosoftLogin = () => {
    const token = DashboardConfig.getAuthToken();
    window.location.href = `${API_BASE_URL}/api/auth/microsoft/login?token=${encodeURIComponent(token || '')}`;
  };

  const hasValidEmail = Boolean(emailStatus?.valid && impersonatedEmail);
  const isLocked = disabled || saving;
  const hasGoogle = Boolean(googleConfig?.configured);
  const hasMicrosoft = true;
  const showProviders = hasGoogle || hasMicrosoft;

  if (googleConfig === null || msConnected === null) return null;
  if (!showProviders) return null;

  const selectedProvider = meetingProvider;

  return (
    <div className="online-meeting-section">
      <div className="online-meeting-provider-toggle">
        <span className="online-meeting-provider-label">Videomøte-leverandør</span>
        <div className="online-meeting-provider-options">
          {hasGoogle && (
            <label className="online-meeting-provider-option">
              <input
                type="radio"
                name="meetingProvider"
                value="google"
                checked={selectedProvider === 'google'}
                disabled={isLocked}
                onChange={() => provider('google')}
                onClick={(e) => {
                  if (selectedProvider === 'google') {
                    e.preventDefault();
                    provider(null);
                  }
                }}
              />
              <span>Google Meet</span>
            </label>
          )}
          {hasMicrosoft && (
            <label className="online-meeting-provider-option">
              <input
                type="radio"
                name="meetingProvider"
                value="microsoft"
                checked={selectedProvider === 'microsoft'}
                disabled={isLocked}
                onChange={() => provider('microsoft')}
                onClick={(e) => {
                  if (selectedProvider === 'microsoft') {
                    e.preventDefault();
                    provider(null);
                  }
                }}
              />
              <span>Microsoft Teams</span>
            </label>
          )}
        </div>
      </div>

      {selectedProvider && (
        <div className="online-meeting-body">
          {/* ---- Google Meet provider ---- */}
          {selectedProvider === 'google' && (
            <div className="online-meeting-email-section">
              {verifying ? (
                <div className="online-meeting-status is-verifying">
                  Verifiserer Workspace-konto{'\u2026'}
                </div>
              ) : hasValidEmail ? (
                <div className="online-meeting-status is-success">
                  <span className="online-meeting-status-icon">&#10003;</span>
                  Kalender: <strong>{impersonatedEmail}</strong>
                </div>
              ) : (
                <div className="online-meeting-status is-error">
                  <p className="online-meeting-status-text">
                    {emailStatus?.email
                      ? `Finner ikke "${emailStatus.email}" i Google Workspace.`
                      : 'Kunne ikke koble til Google Workspace.'}
                  </p>
                  {emailStatus?.error && (
                    <p className="online-meeting-status-text">{emailStatus.error}</p>
                  )}
                  <label className="online-meeting-field">
                    <span>Fra</span>
                    <div className="online-meeting-custom-email-row">
                      <input
                        type="email"
                        className="input"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        onKeyDown={handleCustomKeyDown}
                        disabled={isLocked || customVerifying}
                        placeholder="din@bedrift.no"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={isLocked || customVerifying || !extractEmail(customEmail)}
                        onClick={handleCustomVerify}
                      >
                        {customVerifying ? 'Verifiserer...' : 'Verifiser'}
                      </button>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* ---- Microsoft Teams provider ---- */}
          {selectedProvider === 'microsoft' && (
            <div className="online-meeting-email-section">
              {msConnected ? (
                <div className="online-meeting-status is-success">
                  <span className="online-meeting-status-icon">&#10003;</span>
                  Tilkoblet Microsoft Teams
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={isLocked}
                    onClick={async () => {
                      await DashboardApi.disconnectMicrosoft();
                      setMsConnected(false);
                    }}
                    style={{ marginLeft: 'auto', fontSize: '0.75rem' }}
                  >
                    Koble fra
                  </button>
                </div>
              ) : (
                <div className="online-meeting-status is-error">
                  <p className="online-meeting-status-text">
                    Du må koble til Microsoft Teams for å opprette møter.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isLocked}
                    onClick={handleMicrosoftLogin}
                  >
                    Koble til Microsoft Teams
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ---- Shared fields ---- */}
          <label className="online-meeting-field">
            <span>Beskrivelse</span>
            <textarea
              className="input online-meeting-textarea"
              value={description}
              onChange={(e) => onUpdateDraft({ meetingDescription: e.target.value })}
              disabled={isLocked}
              placeholder="Agenda for m{'\u00F8'}tet..."
              rows={3}
            />
          </label>

          <label className="online-meeting-field">
            <span>Gjester</span>
            <div className="online-meeting-guests">
              {guests.map((email) => (
                <span key={email} className="online-meeting-pill">
                  {email}
                  <button
                    type="button"
                    className="online-meeting-pill-remove"
                    disabled={isLocked}
                    onClick={() => onUpdateDraft({ meetingGuests: guests.filter((g) => g !== email) })}
                    aria-label={`Fjern ${email}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
              {showGuestInput ? (
                <input
                  ref={guestInputRef}
                  type="text"
                  className="online-meeting-guest-input"
                  value={guestInputValue}
                  onChange={(e) => setGuestInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const email = extractEmail(guestInputValue);
                      if (email && isValidEmail(email) && !guests.includes(email)) {
                        onUpdateDraft({ meetingGuests: [...guests, email] });
                      }
                      setGuestInputValue('');
                    }
                    if (e.key === 'Backspace' && !guestInputValue && guests.length > 0) {
                      const next = [...guests];
                      next.pop();
                      onUpdateDraft({ meetingGuests: next });
                    }
                    if (e.key === 'Escape') {
                      setGuestInputValue('');
                      setShowGuestInput(false);
                    }
                  }}
                  onBlur={() => {
                    const email = extractEmail(guestInputValue);
                    if (email && isValidEmail(email) && !guests.includes(email)) {
                      onUpdateDraft({ meetingGuests: [...guests, email] });
                    }
                    setGuestInputValue('');
                    setShowGuestInput(false);
                  }}
                  disabled={isLocked}
                  placeholder="epost@domene.no"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  className="online-meeting-add-guest"
                  disabled={isLocked}
                  onClick={() => setShowGuestInput(true)}
                  aria-label="Legg til gjest"
                >
                  +
                </button>
              )}
            </div>
          </label>

          {savedMeetUrl && (
            <label className="online-meeting-field">
              <span>M{'\u00F8'}telenke</span>
              <div className="online-meeting-link-display">
                <a href={savedMeetUrl} target="_blank" rel="noopener noreferrer">
                  {savedMeetUrl}
                </a>
              </div>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default OnlineMeetingSection;
